import EventTreeVertex from '../EventTreeVertex';
import FunctionalEventVertexValue from './FunctionalEventVertexValue';
import EventTreeMxGraph, {BranchJSON} from "../../../HCLTreeMxGraph/EventTreeMxGraph";
import FunctionalEventSuccessPort from "./Port/FunctionalEventSuccessPort";
import FunctionalEventFailurePort from "./Port/FunctionalEventFailurePort";
import ProxyTypes from "../../HCLTreeVertexValue/ProxyTypes";
import ReferenceTypes from "../../HCLTreeVertexValue/ReferenceTypes";
import Outcome, {OutcomeJSON} from "../../HCLTreeVertexValue/Outcome";
import HCLTreeVertex from "../../HCLTreeVertex";
import EventTreeSuccessEdge from "../EventTreeSuccessEdge";
import EventTreeFailureEdge from "../EventTreeFailureEdge";
import {mxCell, mxConnectionConstraint, mxConstants, mxCylinder, mxGeometry, mxPoint, mxUtils} from "mxgraph-js";

class FunctionalEventVertex extends EventTreeVertex {

  public static SHAPE_KEY = 'functional-event';
  public successPort: FunctionalEventSuccessPort;
  public failurePort: FunctionalEventFailurePort;

  /**
   * Basic shape parameters, used and optionally overridden by child classes
   * @type {{RADIUS: number, WIDTH: number, HEIGHT: number, GATE: number}}
   */
  static SHAPE = {
    HEIGHT: 70,
    WIDTH: 100,
    RADIUS: 10,
    GATE: 0,
  };

  /**
   * @return {() => void}
   */
  static getShape(): () => void {
    function FunctionalEventVertexShape() { mxCylinder.call(this); }
    mxUtils.extend(FunctionalEventVertexShape, mxCylinder);
    FunctionalEventVertexShape.prototype.redrawPath = (c: any, x: any, y: any, w: any, h: any) => {
      const radius = FunctionalEventVertex.SHAPE.RADIUS;
      c.roundrect(0, 0, w, h, radius, radius);
      c.close();
      c.fillAndStroke();
    };
    return FunctionalEventVertexShape;
  }

  /**
   * @param {string} key
   * @param {FunctionalEventVertexValue} value
   */
  constructor(key: string, value: FunctionalEventVertexValue) {
    super(key, value);
  }

  /**
   * @override
   * @return MxGraphType.mxGeometry
   */
  getMyGeometry(): mxGeometry {
    return new mxGeometry(0, 0, FunctionalEventVertex.SHAPE.WIDTH, FunctionalEventVertex.SHAPE.HEIGHT);
  }

  getType(): string {
    return "Functional Event";
  }

  /**
   * @override
   * @return {[index: string]: any}
   */
  getMyStyle(): {[index: string]: any} {
    const style: {[index: string]: any} = {};
    style[mxConstants.STYLE_SPACING] = 6;
    style[mxConstants.STYLE_FILLCOLOR] = 'rgba(146,158,209,0.21)';
    style[mxConstants.STYLE_STROKECOLOR] = 'rgba(52,100,151,0.75)';
    style[mxConstants.STYLE_FILL_OPACITY] = '50';
    style[mxConstants.STYLE_STROKE_OPACITY] = '50';
    style[mxConstants.STYLE_STROKEWIDTH] = '2';
    style[mxConstants.STYLE_FONTCOLOR] = 'inherit';
    style[mxConstants.STYLE_FONTSIZE] = '12';
    style[mxConstants.STYLE_FONTFAMILY] = 'Roboto Condensed';
    style[mxConstants.STYLE_SHAPE] = FunctionalEventVertex.SHAPE_KEY;
    style[mxConstants.STYLE_SPACING_BOTTOM] = FunctionalEventVertex.SHAPE.GATE;
    style[mxConstants.STYLE_WHITE_SPACE] = 'wrap';
    style[mxConstants.STYLE_AUTOSIZE] = 1;
    style[mxConstants.STYLE_RESIZABLE] = 0;
    style[mxConstants.STYLE_ASPECT] = 'fixed';
    style[mxConstants.STYLE_OVERFLOW] = 'hidden';
    return style;
  }


  /**
   * Define an return an array of source connection constraints for a functional/pivotal event. Constrains outgoing
   * edges to either the bottom,middle section or the middle,rightmost section of the vertex.
   * @return MxGraphType.mxConnectionConstraint[]
   */
  getSourceConnectionConstraints(): mxConnectionConstraint[] {
    return [
      new mxConnectionConstraint(new mxPoint(1, 0.5), true),
      new mxConnectionConstraint(new mxPoint(0.5, 1), true)
    ];
  }

  /**
   * Define an return a singleton array of target connection constraints for a functional/pivotal event. Constrains
   * incoming edges to the leftmost,middle section of the vertex.
   * @return MxGraphType.mxConnectionConstraint[]
   */
  getTargetConnectionConstraints(): mxConnectionConstraint[] {
    return [
      new mxConnectionConstraint(new mxPoint(0, 0.5), true),
    ];
  }

  onAddedToGraph(graph: EventTreeMxGraph) {
    super.onAddedToGraph(graph);
    this.setConnectable(true);
    this.successPort = graph.addVertex(new FunctionalEventSuccessPort(), this) as FunctionalEventSuccessPort;
    this.failurePort = graph.addVertex(new FunctionalEventFailurePort(), this) as FunctionalEventFailurePort;
  }

  willRemoveFromGraph(graph: EventTreeMxGraph) {
    super.willRemoveFromGraph(graph);
  }

  setBranchKey(key: string) {

  }

  toBranchJSON(): { outcome: BranchJSON } {

    const value = this.getValue() as FunctionalEventVertexValue;

    return ({
      outcome: {
        [ReferenceTypes.FUNCTIONAL_EVENT]: this.getKey(),
        [ReferenceTypes.PATHS]: {
          failure: {
            ...this.failurePort.toJSON(),
            instructions: value.getInstructions().map(instruction => instruction.toJSON())
          },
          success: {
            ...this.successPort.toJSON(),
            instructions: value.getInstructions().map(instruction => instruction.inverse().toJSON())
          }
        },
        _proxy: ProxyTypes.FORK
      }
    });
  }

  toReferenceJSON(): OutcomeJSON {
    return new Outcome(
      (this.getValue() as FunctionalEventVertexValue).getBranchKey(),
      ReferenceTypes.BRANCHES,
      this.getGraph().getModelTreeId(),
      "",
      ProxyTypes.END_STATE
      ).toJSON();
  }

  clone(): FunctionalEventVertex {
    return new FunctionalEventVertex(null, this.value.clone());
  }

  onRequestConnectChildVertex(vertex: HCLTreeVertex): mxCell {
    const graph = this.getGraph();
    if (!this.successPort.edges || !this.successPort.edges.length) {
      return graph.insertEdgeBetween(new EventTreeSuccessEdge(), this.successPort, vertex);
    } else if (!this.failurePort.edges || !this.failurePort.edges.length) {
      return graph.insertEdgeBetween(new EventTreeFailureEdge(), this.failurePort, vertex);
    }
    return null;
  }
}

export default FunctionalEventVertex;
